import { useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProgressRing from "@/components/ui/progress-ring";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter } from "@/components/ui/modal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";

export default function UILab() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("design");
  const [loading, setLoading] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const { toast } = useToast();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-bold">UI Lab</h1>
          <Link href="/">
            <a className="inline-flex items-center gap-2 text-primary font-semibold">
              Back to site
              <ArrowRight className="h-4 w-4" />
            </a>
          </Link>
        </div>

        <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Buttons</h2>
            <div className="flex flex-wrap gap-3">
              <Button>Primary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button disabled>Disabled</Button>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button disabled={loading}>
                {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving</>) : "Save"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (loading) return;
                  setLoading(true);
                  setTimeout(() => setLoading(false), 1200);
                }}
              >
                Trigger loading
              </Button>
            </div>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Badges</h2>
            <div className="flex flex-wrap gap-3">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
            </div>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Progress</h2>
            <div className="flex items-center gap-6">
              <ProgressRing value={68} />
              <ProgressRing value={92} size={64} stroke={6} label={<span className="text-xs">92%</span>} />
              <div className="flex-1">
                <Progress value={45} />
              </div>
            </div>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Toast</h2>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => toast({ title: "Saved", description: "Your settings were updated." })}>Show toast</Button>
              <Button variant="outline" onClick={() => toast({ title: "Heads up", description: "This is an informational notice." })}>Info toast</Button>
            </div>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Avatars & Tooltip</h2>
            <div className="flex items-center gap-4">
              <Avatar>
                <AvatarImage src="/coach-avatars/Rohan_Performance_Coach_Avatar_450e031f.png" alt="Samuel" />
                <AvatarFallback>S</AvatarFallback>
              </Avatar>
              <Avatar>
                <AvatarImage src="/coach-avatars/missing.png" alt="Fallback" />
                <AvatarFallback>HP</AvatarFallback>
              </Avatar>
              <Tooltip content="Helpful tooltip">
                <Button variant="outline">Hover me</Button>
              </Tooltip>
            </div>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Tabs</h2>
            <Tabs defaultValue="design" value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="design">Design</TabsTrigger>
                <TabsTrigger value="code">Code</TabsTrigger>
              </TabsList>
              <TabsContent value="design">
                <p className="text-sm text-muted-foreground">Design system primitives and tokens.</p>
              </TabsContent>
              <TabsContent value="code">
                <p className="text-sm text-muted-foreground">TypeScript, a11y, and state management.</p>
              </TabsContent>
            </Tabs>
          </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
            whileHover={prefersReducedMotion ? {} : { scale: 1.01, transition: { duration: 0.15 } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Textarea & Modal</h2>
            <div className="space-y-3">
              <Textarea placeholder="Leave a note..." />
              <div>
                <Button onClick={() => setOpen(true)}>Open Modal</Button>
              </div>
            </div>
            <Modal open={open} onOpenChange={setOpen}>
              <ModalContent>
                <ModalHeader>
                  <ModalTitle>Example modal</ModalTitle>
                  <ModalDescription>Token-driven, accessible, and keyboard dismissible (Esc).</ModalDescription>
                </ModalHeader>
                <ModalFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={() => setOpen(false)}>Confirm</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </Card>
          </motion.div>

          <motion.div
            className="col-span-full"
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: prefersReducedMotion ? 0 : 0.3, ease: [0.2, 0.8, 0.2, 1] } }}
          >
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Typography & Colors</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h1 className="font-display text-4xl">Display 4xl</h1>
                <h2 className="font-display text-3xl">Display 3xl</h2>
                <h3 className="font-display text-2xl">Display 2xl</h3>
                <p className="mt-2 text-muted-foreground">Body text with muted foreground for long-form readability.</p>
              </div>
              <div className="grid grid-cols-6 gap-2">
                <div className="h-10 rounded bg-primary" />
                <div className="h-10 rounded bg-accent" />
                <div className="h-10 rounded bg-muted" />
                <div className="h-10 rounded bg-secondary" />
                <div className="h-10 rounded bg-destructive" />
                <div className="h-10 rounded bg-ring" />
              </div>
            </div>
          </Card>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
